import React, { useRef, useCallback, useEffect, useState } from 'react';
import { useController } from 'react-hook-form';
import { v4 } from 'uuid';

/**
 * DateOfBirth component that wraps the GDS date-of-birth web component
 * for use with react-hook-form. Provides form validation and error handling
 * for date of birth input fields.
 * 
 * @component
 * @param {Object} props - The component props
 * @param {Object} props.control - React Hook Form control object for form state management
 * @param {string} props.name - The name/key for this field in the form data
 * @param {string} [props.label="Date of Birth"] - The label text displayed for the input field
 * @param {string} [props.descriptiveCopy] - Optional descriptive text displayed below the input
 * @param {boolean} [props.showTooltip=true] - Whether to display the privacy policy tooltip
 * @returns {JSX.Element} The rendered DateOfBirth component
 * 
 * @example
 * // Basic usage with react-hook-form
 * <DateOfBirth 
 *   control={control} 
 *   name="dateOfBirth" 
 * />
 * 
 * @example
 * // With custom label and descriptive copy
 * <DateOfBirth 
 *   control={control} 
 *   name="birthDate"
 *   label="Your Date of Birth"
 *   descriptiveCopy="Please enter your birth date in MM/DD/YYYY format"
 * />
 */
export default function DateOfBirth({
    control,
    name,
    label = "Date of Birth",
    descriptiveCopy,
    showTooltip = true
}) {
    /** @type {React.MutableRefObject<HTMLElement>} Reference to the gds-date-of-birth web component */
    const gdsDateOfBirthRef = useRef();

    /**
     * React Hook Form controller for managing field state, validation, and error handling
     * @type {Object} field - Field methods and properties from react-hook-form
     * @type {Object} fieldState - Field state including error information
     */
    const { field, fieldState: { error } } = useController({
        control,
        name
    });

    /**
     * State to track if component has mounted to ensure GDS component is initialized
     * before calling setDob method. Prevents calling setDob before iMaskInstance is ready.
     * @type {boolean}
     */
    const [didMount, setDidMount] = useState(false);

    /**
     * Effect to set the component as mounted on first render
     * This ensures the GDS component is fully initialized before we interact with it
     */
    useEffect(() => { setDidMount(true); }, []);

    /**
     * Effect to set the date of birth value on the GDS component when field value changes
     * Only executes after component has mounted and when value is a string
     */
    useEffect(() => {
        if (didMount && (typeof field?.value === 'string' || field?.value instanceof String)) {
            gdsDateOfBirthRef.current?.setDob?.(field.value);
        }
    }, [field.value, didMount]);

    /**
     * Handles input value changes from the GDS date-of-birth component
     * Trims the value to 10 characters to remove any extra characters that may appear
     * 
     * @param {CustomEvent} e - The input-value-change event from the GDS component
     * @param {Object} e.detail - Event detail object
     * @param {string} e.detail.value - The new input value
     */
    const handleInputValueChange = useCallback((e) => {
        field.onChange(e.detail.value?.substring(0, 10)); // necessary to take substring because sometimes an extra character appears at the end
    }, [field.onChange]);

    /**
     * Effect to set up and clean up event listener for input value changes
     * Listens to the 'input-value-change' custom event from the GDS component
     */
    useEffect(() => {
        gdsDateOfBirthRef.current?.addEventListener("input-value-change", handleInputValueChange);
        return () => {
            gdsDateOfBirthRef.current?.removeEventListener("input-value-change", handleInputValueChange);
        };
    }, [handleInputValueChange]);

    return (
        <gds-date-of-birth
            ref={gdsDateOfBirthRef}
            input-id={`dob-${v4()}`}
        >
            {label}

            {showTooltip && (
                <gds-tooltip>
                    At GEICO, we understand the importance of your privacy and security. The information you
                    provide will be kept confidential and secure. It will not be sold or redistributed. To learn
                    more about how we protect your personal information, please review our privacy &amp; security
                    policy: <a href="https://media.geico.com/legal/privacy_policy.htm" target="_blank"
                        className=""> Privacy Policy</a>
                </gds-tooltip>
            )}

            {descriptiveCopy &&
                <span slot="descriptive-text">{descriptiveCopy}</span>
            }

            {error &&
                <span slot="error-message-text">{error.message}</span>
            }
        </gds-date-of-birth>
    );
}
